import classNames from "classnames";
import {forwardRef, useContext} from "react";

import {Card} from "@/components/card/Card.tsx";
import {CheckboxInput} from "@/components/checkbox/CheckboxInput/CheckboxInput.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {SelectionCardContext} from "@/components/selectionCard/SelectionCard.context.ts";
import {SelectionCardOptionProps} from "@/components/selectionCard/subComponents/SelectionCardOption/SelectionCardOption.types.ts";

import styles from "./SelectionCardOption.module.scss";

export const SelectionCardOption = forwardRef<HTMLDivElement, SelectionCardOptionProps>(
  (
    {
      value,
      icon,
      imageSrc,
      imageAlt,
      title,
      description,
      children,
      disabled = false,
      labelPosition = "left",
      radioVariant = "selectionCard",
      className,
      ...rest
    },
    ref
  ) => {
    const context = useContext(SelectionCardContext);
    const selected = context?.value === value;

    const handleClick = () => {
      if (!disabled && context?.onChange) {
        context.onChange(value);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
      rest.onKeyDown?.(e);
    };

    const checkboxInputControl = (
      <CheckboxInput
        type="radio"
        variant={radioVariant}
        checked={selected}
        disabled={disabled}
        readOnly
        tabIndex={-1}
        className={styles.radio}
      />
    );

    return (
      <Card
        ref={ref}
        variant="selectionCard"
        active={selected}
        role="radio"
        aria-checked={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={classNames(styles.option, {[styles.disabled]: disabled}, className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        <div className="d-flex flex-row justify-content-between gap-4">
          {labelPosition === "right" && checkboxInputControl}
          <div className="d-flex flex-column gap-2 w-100">
            {icon && <Icon icon={icon} size="normal" />}
            {imageSrc && <img className={styles.previewImage} src={imageSrc} alt={imageAlt ?? `${title} preview`} />}
            <span className={styles.title}>{title}</span>
            {description && <span className={styles.description}>{description}</span>}
          </div>
          {labelPosition === "left" && checkboxInputControl}
        </div>
        {children}
      </Card>
    );
  }
);

SelectionCardOption.displayName = "SelectionCardOption";
